/* eslint-disable no-unused-vars */
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Home, Eye, EyeOff, RefreshCw, LogOut, Edit2, Save, X, Upload, Download, Volume2, Sparkles, Hash, Trophy, FileDown, FileUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { API, BACKEND_URL, resolveUrl, useAdmin } from "./adminShared";
import { PlacementEditor } from "./PlacementEditor";
import { VotingColumnEditor } from "./VotingColumnEditor";
import { TextsOverlaysSection } from "./config/TextsOverlaysSection";
import { VoterSubtitleSection } from "./config/VoterSubtitleSection";
import { LayoutSection } from "./config/LayoutSection";
import { TypographySection } from "./config/TypographySection";
import { ColorsSection } from "./config/ColorsSection";
import { ImagesSection } from "./config/ImagesSection";
import { MediaSection } from "./config/MediaSection";
import { PreviewExportSection } from "./config/PreviewExportSection";

export const ConfigTab = () => {
  const {
    countries, setCountries, votes, setVotes, events, setEvents,
    activeEvent, setActiveEvent, showCreateEvent, setShowCreateEvent, newEventName, setNewEventName,
    copyFromEventId, setCopyFromEventId, creatingEvent, setCreatingEvent, showRenameEvent, setShowRenameEvent,
    renameEventName, setRenameEventName, importFile, setImportFile, showImportConfirm, setShowImportConfirm,
    importing, setImporting, newCountry, setNewCountry, editingCountry, setEditingCountry,
    backgroundVideo, setBackgroundVideo, bgVideoBlur, setBgVideoBlur, bgVideoBrightness, setBgVideoBrightness, bgVideoOpacity, setBgVideoOpacity, fontTitle, setFontTitle, fontSubtitle, setFontSubtitle,
    fontCountry, setFontCountry, fontPoints, setFontPoints, fontVoting, setFontVoting,
    customFontTitleUrl, setCustomFontTitleUrl, customFontTitleName, setCustomFontTitleName, customFontSubtitleUrl, setCustomFontSubtitleUrl,
    customFontSubtitleName, setCustomFontSubtitleName, customFontCountryUrl, setCustomFontCountryUrl, customFontCountryName, setCustomFontCountryName,
    customFontPointsUrl, setCustomFontPointsUrl, customFontPointsName, setCustomFontPointsName, customFontVotingUrl, setCustomFontVotingUrl,
    customFontVotingName, setCustomFontVotingName, fontSizeTitle, setFontSizeTitle, fontSizeSubtitle, setFontSizeSubtitle,
    fontSizeCountry, setFontSizeCountry, fontSizePoints, setFontSizePoints, fontSizeVoting, setFontSizeVoting,
    primaryColor, setPrimaryColor, secondaryColor, setSecondaryColor, tertiaryColor, setTertiaryColor,
    rowBgEven, setRowBgEven, rowBgOdd, setRowBgOdd, rowBgEvenTranslucent, setRowBgEvenTranslucent, rowBgOddTranslucent, setRowBgOddTranslucent, rowBadgeTranslucent, setRowBadgeTranslucent, logoUrl, setLogoUrl,
    logoPosition, setLogoPosition, logoSize, setLogoSize, logoZIndex, setLogoZIndex,
    logoOpacity, setLogoOpacity, extraImage1Url, setExtraImage1Url, extraImage1Position, setExtraImage1Position,
    extraImage1Size, setExtraImage1Size, extraImage1ZIndex, setExtraImage1ZIndex, extraImage1Opacity, setExtraImage1Opacity,
    extraImage2Url, setExtraImage2Url, extraImage2Position, setExtraImage2Position, extraImage2Size, setExtraImage2Size,
    extraImage2ZIndex, setExtraImage2ZIndex, extraImage2Opacity, setExtraImage2Opacity, extraImage3Url, setExtraImage3Url,
    extraImage3Position, setExtraImage3Position, extraImage3Size, setExtraImage3Size, extraImage3ZIndex, setExtraImage3ZIndex,
    extraImage3Opacity, setExtraImage3Opacity, defaultArtistPhoto, setDefaultArtistPhoto, artistPhotoSizeMode, setArtistPhotoSizeMode,
    artistPhotoSizePx, setArtistPhotoSizePx, textTitle, setTextTitle, textSubtitle, setTextSubtitle,
    textVotingNow, setTextVotingNow, textWaiting, setTextWaiting, sound12PointsUrl, setSound12PointsUrl,
    confettiEnabled, setConfettiEnabled, confettiColors, setConfettiColors, top1Color, setTop1Color,
    top2Color, setTop2Color, top3Color, setTop3Color, lastPlaceColor, setLastPlaceColor,
    top1Translucent, setTop1Translucent, top2Translucent, setTop2Translucent, top3Translucent, setTop3Translucent,
    lastPlaceTranslucent, setLastPlaceTranslucent, positionBadgeTranslucent, setPositionBadgeTranslucent, votingColumnCenter, setVotingColumnCenter, leftColSpan, setLeftColSpan,
    rightColSpan, setRightColSpan, votingColSpan, setVotingColSpan, columnGap, setColumnGap,
    subtitleCounterEnabled, setSubtitleCounterEnabled, subtitlePhaseOrder, setSubtitlePhaseOrder, subtitleJuryLabel, setSubtitleJuryLabel,
    subtitleTelevoteLabel, setSubtitleTelevoteLabel, mystery12Text, setMystery12Text, mysteryHiddenPoints, setMysteryHiddenPoints, voteDisplayColumns, setVoteDisplayColumns,
    showVoterBorder, setShowVoterBorder, showJuryBadge, setShowJuryBadge, fontVotingSecondary, setFontVotingSecondary,
    fontTitleItalic, setFontTitleItalic, fontSubtitleItalic, setFontSubtitleItalic, fontCountryItalic, setFontCountryItalic,
    fontPointsItalic, setFontPointsItalic, fontVotingItalic, setFontVotingItalic, fontVotingSecondaryItalic, setFontVotingSecondaryItalic,
    customFontVotingSecondaryUrl, setCustomFontVotingSecondaryUrl, customFontVotingSecondaryName, setCustomFontVotingSecondaryName, overlay12Enabled, setOverlay12Enabled,
    overlay12DurationMode, setOverlay12DurationMode, overlay12DurationSeconds, setOverlay12DurationSeconds, overlay12BgStyle, setOverlay12BgStyle,
    overlay12BgColor, setOverlay12BgColor, overlay12BgOpacity, setOverlay12BgOpacity, overlay12TextReceives, setOverlay12TextReceives,
    overlay12UseAdminColors, setOverlay12UseAdminColors, overlay12ShowConfetti, setOverlay12ShowConfetti, overlay12Visible, setOverlay12Visible, previewGiverId, setPreviewGiverId,
    previewRecipientId, setPreviewRecipientId, winnerOverlayEnabled, setWinnerOverlayEnabled, winnerOverlayDurationMode, setWinnerOverlayDurationMode,
    winnerOverlayDurationSeconds, setWinnerOverlayDurationSeconds, winnerOverlayBgStyle, setWinnerOverlayBgStyle, winnerOverlayBgColor, setWinnerOverlayBgColor,
    winnerOverlayBgOpacity, setWinnerOverlayBgOpacity, winnerOverlayTitleText, setWinnerOverlayTitleText, winnerOverlaySubtitleText, setWinnerOverlaySubtitleText,
    winnerOverlayUseAdminColors, setWinnerOverlayUseAdminColors, winnerOverlayShowConfetti, setWinnerOverlayShowConfetti, winnerOverlayShowPoints, setWinnerOverlayShowPoints,
    winnerOverlayVisible, setWinnerOverlayVisible, overlay12StyleCustom, setOverlay12StyleCustom, overlay12FontTitle, setOverlay12FontTitle,
    overlay12FontCountry, setOverlay12FontCountry, overlay12FontSecondary, setOverlay12FontSecondary, overlay12SizeScale, setOverlay12SizeScale,
    winnerOverlayStyleCustom, setWinnerOverlayStyleCustom, winnerOverlayFontTitle, setWinnerOverlayFontTitle, winnerOverlayFontCountry, setWinnerOverlayFontCountry,
    winnerOverlayFontSecondary, setWinnerOverlayFontSecondary, winnerOverlaySizeScale, setWinnerOverlaySizeScale, loading, setLoading,
    exportingPdf, setExportingPdf, winnerPreviewId, setWinnerPreviewId, adminInitRef, adminPollRef,
    navigate, fetchCurrentVoterStatus, handleHideOverlay12, handlePreviewOverlay12, handleShowWinner, handleHideWinner,
    getFontFormat, fetchBackground, handleSetBackground, handleExportPdf, handleFileUpload, fetchCountries,
    fetchVotes, downloadFromApi, handleExportVotesPdf, handleExportVotesExcel, fetchEvents, reloadEventData,
    handleSelectEvent, handleCreateEvent, handleRenameEvent, handleExportEvent, handleImportEvent, handleAddCountry,
    handleDeleteCountry, handleSetCurrentVoter, handleResetVotes, handleLogout, handleApplyVotes, handleReveal12Points,
    handleEditCountry, handleUpdateCountry, handleToggleBadge, handleToggleTranslucent, votingCountries, juryMembers,
    votedCountries,
  } = useAdmin();
  return (
          <TabsContent value="config" className="space-y-4 mt-6">
            <Card className="bg-zinc-900/80 border-zinc-800 p-6">
              <h2 className="font-unbounded text-xl text-white mb-4">Configuración del Visualizador</h2>
              <p className="text-zinc-400 mb-6">Personaliza completamente la pantalla pública</p>
              
              <div className="space-y-6">
                <TextsOverlaysSection />
                <VoterSubtitleSection />
                <LayoutSection />
                <TypographySection />
                <ColorsSection />
                <ImagesSection />
                <MediaSection />
                <PreviewExportSection />

                {/* Botón Aplicar */}
                <Button
                  data-testid="btn-apply-config"
                  onClick={handleSetBackground}
                  className="w-full h-14 bg-gradient-to-r from-pink-600 to-cyan-600 hover:from-pink-500 hover:to-cyan-500 text-white font-unbounded font-semibold text-lg"
                >
                  Aplicar Toda la Configuración
                </Button>

                <Button
                  data-testid="btn-migrate-urls"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/migrate-urls`, { new_base_url: BACKEND_URL });
                      toast.success("URLs migradas al dominio actual");
                      fetchBackground();
                      fetchCountries();
                    } catch (err) {
                      toast.error("Error al migrar URLs");
                    }
                  }}
                  variant="outline"
                  className="w-full h-10 border-amber-600 text-amber-400 hover:bg-amber-900/30"
                >
                  Migrar URLs al dominio actual
                </Button>

                <div className="p-4 bg-blue-900/20 rounded-lg border border-blue-800">
                  <p className="text-blue-300 text-sm">
                    💡 <strong>Tip:</strong> Los cambios se aplican inmediatamente en el visualizador. Recarga la página del visualizador para ver los cambios.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>
  );
};
